import { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ConditionQuickInsert from "./ConditionQuickInsert";
import { getConditionOptions } from "./workflow.types";
import type { ConditionFieldDef, ConditionOption, WorkflowEdge, WorkflowNode, NodeDef } from "./workflow.types";

const MAX_OPTIONS = 6;
const MIN_OPTIONS = 2;

function describeTarget(
  node: WorkflowNode,
  port: string,
  edges: WorkflowEdge[],
  nodes: WorkflowNode[],
  nodeDefsById: Record<string, NodeDef>,
): string {
  const edge = edges.find((e) => e.source === node.id && e.sourcePort === port);
  if (!edge) return "Not connected";
  const target = nodes.find((n) => n.id === edge.target);
  if (!target) return "Not connected";
  return target.config.label || nodeDefsById[target.subtype]?.label || target.subtype;
}

function makeOptionId(): string {
  return `opt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

const OPERATOR_LABELS: Record<string, string> = {
  is: "is",
  is_not: "is not",
  contains: "contains",
  gt: "is greater than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  is_empty: "is empty",
  is_not_empty: "is not empty",
};

/** Which operators make sense for a field, based on whether it has a fixed option list and its data type. */
function operatorsForField(field: ConditionFieldDef | undefined): string[] {
  if (!field) return ["is", "is_not"];
  if (field.options?.length) return ["is", "is_not", "is_empty", "is_not_empty"];
  if (field.dataType === "number" || field.dataType === "date") {
    return ["is", "is_not", "gt", "gte", "lt", "lte", "is_empty", "is_not_empty"];
  }
  return ["is", "is_not", "contains", "is_empty", "is_not_empty"];
}

/**
 * The condition node's editor — two distinct evaluation modes sharing one Yes/No
 * wiring summary below. 'field' is the IFTTT-style deterministic mode (no LLM,
 * a direct Meeting/Client field comparison); 'ai' is the original free-text
 * reasoning mode. Both write into the same WorkflowNodeConfig — the runner picks
 * which one to actually evaluate based on `config.conditionMode`.
 */
export default function ConditionEditor({
  node,
  nodes,
  edges,
  nodeDefsById,
  conditionFields,
  onChange,
}: {
  node: WorkflowNode;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  nodeDefsById: Record<string, NodeDef>;
  conditionFields: ConditionFieldDef[];
  onChange: (patch: Partial<WorkflowNode["config"]>) => void;
}) {
  const mode = node.config.conditionMode ?? "ai";
  const selectedField = conditionFields.find((f) => f.key === node.config.field);
  const options = getConditionOptions(node.config);
  const availableOperators = operatorsForField(selectedField);
  const operator = node.config.operator ?? "is";
  const needsValue = operator !== "is_empty" && operator !== "is_not_empty";

  // "Compare to a previous step's result" is a display-mode toggle only — the
  // stored value is the same {{steps.x.output.y}} template either way, just
  // rendered as free text instead of the field's structured input. Inferred
  // from the current value's shape rather than a separate stored flag.
  const [useStepValue, setUseStepValue] = useState(() => /^\{\{\s*steps\./.test(node.config.value ?? ""));

  const handleOptionLabelChange = (id: string, label: string) => {
    onChange({ options: options.map((o) => (o.id === id ? { ...o, label } : o)) });
  };

  const handleAddOption = () => {
    if (options.length >= MAX_OPTIONS) return;
    const next: ConditionOption[] = [...options, { id: makeOptionId(), label: `Option ${options.length + 1}` }];
    onChange({ options: next });
  };

  const handleRemoveOption = (id: string) => {
    if (options.length <= MIN_OPTIONS) return;
    onChange({ options: options.filter((o) => o.id !== id) });
  };

  return (
    <>
      <TextField
        label="Step label"
        fullWidth
        size="small"
        value={node.config.label ?? ""}
        onChange={(e) => onChange({ label: e.target.value })}
        placeholder="e.g. Was this a Teams meeting?"
        sx={{ mb: 2 }}
      />
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_, next) => next && onChange({ conditionMode: next })}
        sx={{ mb: 2, width: "100%" }}
      >
        <ToggleButton value="ai" sx={{ flex: 1, textTransform: "none" }}>
          AI reasoning
        </ToggleButton>
        <ToggleButton value="field" sx={{ flex: 1, textTransform: "none" }}>
          Field comparison
        </ToggleButton>
      </ToggleButtonGroup>

      {mode === "field" ? (
        <>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            A direct, instant check on a real field — no AI call, always the same answer for the same
            data. Good for "if this, then that" branching on something you already know, like the
            meeting's medium or the client's type.
          </Typography>
          <TextField
            select
            label="If"
            fullWidth
            size="small"
            value={node.config.field ?? ""}
            onChange={(e) => onChange({ field: e.target.value, value: "" })}
            sx={{ mb: 2 }}
          >
            {conditionFields.map((f) => (
              <MenuItem key={f.key} value={f.key}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              select
              label="Operator"
              size="small"
              sx={{ minWidth: 150 }}
              value={availableOperators.includes(operator) ? operator : "is"}
              onChange={(e) => onChange({ operator: e.target.value as typeof node.config.operator })}
            >
              {availableOperators.map((op) => (
                <MenuItem key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </MenuItem>
              ))}
            </TextField>
            {needsValue && !useStepValue && selectedField?.options ? (
              // freeSolo Autocomplete, not a strict select — the field's own
              // options are offered as suggestions, but any value can still
              // be typed directly (e.g. a value not in the fixed list yet, or
              // one that only applies to some clients).
              <Autocomplete
                size="small"
                fullWidth
                freeSolo
                disabled={!selectedField}
                options={selectedField.options}
                value={node.config.value ?? ""}
                inputValue={node.config.value ?? ""}
                onChange={(_, next) => onChange({ value: next ?? "" })}
                onInputChange={(_, next, reason) => {
                  if (reason === "input") onChange({ value: next });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Value"
                    error={!!selectedField && !node.config.value}
                    helperText={
                      selectedField && !node.config.value
                        ? "Please enter this value"
                        : "Pick a suggestion or type your own"
                    }
                  />
                )}
              />
            ) : needsValue ? (
              <TextField
                label="Value"
                size="small"
                fullWidth
                type={useStepValue ? "text" : selectedField?.dataType === "date" ? "date" : selectedField?.dataType === "number" ? "number" : "text"}
                disabled={!selectedField}
                value={node.config.value ?? ""}
                onChange={(e) => onChange({ value: e.target.value })}
                placeholder={useStepValue ? "e.g. {{steps.step-id.output.fieldName}}" : undefined}
                error={!!selectedField && !node.config.value}
                helperText={selectedField && !node.config.value ? "Please enter this value" : undefined}
                InputLabelProps={selectedField?.dataType === "date" && !useStepValue ? { shrink: true } : undefined}
              />
            ) : null}
          </Box>
          {selectedField && (
            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Checkbox
                  size="small"
                  checked={useStepValue}
                  onChange={(e) => setUseStepValue(e.target.checked)}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Compare to a previous step's result instead of a fixed value
                </Typography>
              }
            />
          )}
          {selectedField && node.config.value && needsValue && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              "If {selectedField.label} {OPERATOR_LABELS[operator] || operator} '{node.config.value}'"
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              label="Yes label"
              size="small"
              value={node.config.yesLabel ?? "Yes"}
              onChange={(e) => onChange({ yesLabel: e.target.value })}
            />
            <TextField
              label="No label"
              size="small"
              value={node.config.noLabel ?? "No"}
              onChange={(e) => onChange({ noLabel: e.target.value })}
            />
          </Box>
          <Typography variant="caption" display="block" color="text.secondary">
            → {node.config.yesLabel || "Yes"} leads to: {describeTarget(node, "yes", edges, nodes, nodeDefsById)}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            → {node.config.noLabel || "No"} leads to: {describeTarget(node, "no", edges, nodes, nodeDefsById)}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Write your own condition below, or use these to build one — each pick adds a clause rather
            than replacing what you've written.
          </Typography>
          <ConditionQuickInsert value={node.config.prompt ?? ""} onInsert={(next) => onChange({ prompt: next })} />
          <TextField
            label="Condition"
            fullWidth
            multiline
            minRows={4}
            value={node.config.prompt ?? ""}
            onChange={(e) => onChange({ prompt: e.target.value })}
            placeholder="e.g. Classify this meeting as Retiring soon, Still working, or Unclear based on what the client said about their plans."
            sx={{ mb: 2 }}
          />

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Outcomes — the model picks exactly one of these for each run, based on the condition above.
            Not limited to two; add as many as this decision actually needs.
          </Typography>
          {options.map((option, index) => (
            <Box key={option.id} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <TextField
                size="small"
                fullWidth
                value={option.label}
                onChange={(e) => handleOptionLabelChange(option.id, e.target.value)}
                placeholder={`Outcome ${index + 1}`}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveOption(option.id)}
                disabled={options.length <= MIN_OPTIONS}
                aria-label="Remove outcome"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={handleAddOption}
            disabled={options.length >= MAX_OPTIONS}
            sx={{ textTransform: "none", mb: 2 }}
          >
            Add outcome
          </Button>

          {options.map((option) => (
            <Typography key={option.id} variant="caption" display="block" color="text.secondary">
              → {option.label || "Untitled"} leads to: {describeTarget(node, option.id, edges, nodes, nodeDefsById)}
            </Typography>
          ))}
        </>
      )}
    </>
  );
}
